import { compactArray } from '@mews/fp';
import { EnterpriseScopeCounterDiscriminator, useRepositoryMutation } from '@mews-commander/data-access';
import { lt, showSuccessAlert } from '@mews/framework';
import { createBooleanUpdateValue, createUpdateValue } from '@mews-commander/utils';
import { useCounter } from '../../queries/useCounters';
import { CreditNoteFormValues } from '../../constants';
import { CounterFormField, CounterUseFor } from '../CounterForm/formMapping';
import { convertUseForFormValueToApplicableBillType } from '../../utils';
import { CreditNoteCounterDialog, CreditNoteCounterDialogProps } from './CreditNoteCounterDialog';

interface Props extends Omit<CreditNoteCounterDialogProps, 'isEditForm' | 'defaultValues' | 'onSubmit' | 'isDefault'> {
    id: string;
}

export const UpdateCreditNoteCounterDialog = ({ active, handleClose, id }: Props) => {
    const { data: counter } = useCounter(id);
    const { mutateAsync } = useRepositoryMutation('creditNoteBillCounters', 'update', {
        invalidateQueries: { counters: ['getAll'] },
    });

    if (!counter || !active) {
        return null;
    }

    if (counter.data.discriminator !== EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter) return null;

    const defaultValues: CreditNoteFormValues = {
        [CounterFormField.Title]: counter.data.value.title,
        [CounterFormField.Name]: counter.name,
        [CounterFormField.StartValue]: counter.value,
        [CounterFormField.Prefix]: counter.data.value.prefix || null,
        [CounterFormField.DisplayCID]: counter.data.value.displayCid,
        [CounterFormField.BillTypeCode]: counter.data.value.billTypeCode,
        [CounterFormField.IsDefault]: counter.isDefault,
        [CounterFormField.NumberOfDigits]: counter.data.value.numberOfDigits,
        [CounterFormField.UseFor]: compactArray([
            counter.data.value.applicableBillType.applyToInvoice ? CounterUseFor.invoices : null,
            counter.data.value.applicableBillType.applyToBill ? CounterUseFor.bills : null,
        ]),
        [CounterFormField.Created]: counter.createdUtc,
        [CounterFormField.Updated]: counter.updatedUtc,
        [CounterFormField.CounterId]: counter.id,
    };

    const onSubmit = async (values: CreditNoteFormValues) => {
        showSuccessAlert({ description: lt('UpdateCounterSuccessMessage') });

        const newBillType = convertUseForFormValueToApplicableBillType(values.useFor);
        const defaultBillType = convertUseForFormValueToApplicableBillType(defaultValues.useFor);

        await mutateAsync({
            counterId: counter.id,
            prefix: createUpdateValue(defaultValues.prefix, values.prefix),
            displayCid: createBooleanUpdateValue(defaultValues.displayCID, values.displayCID),
            name: createUpdateValue(defaultValues.name, values.name),
            numberOfDigits: {
                value: values.numberOfDigits,
            },
            title: createUpdateValue(defaultValues.title, values.title),
            billTypeCode: createUpdateValue(defaultValues.billTypeCode, values.billTypeCode),
            isDefault: createBooleanUpdateValue(defaultValues.isDefault, values.isDefault),
            applicableBillType: createUpdateValue(defaultBillType, newBillType),
        });
        handleClose();
    };

    return (
        <CreditNoteCounterDialog
            isEditForm
            active={active}
            onSubmit={onSubmit}
            defaultValues={defaultValues}
            handleClose={handleClose}
        />
    );
};
